import {
  Box,
  Skeleton,
  Grid,
  GridItem,
  Flex,
  Text,
  Divider,
  Spacer,
  useToast,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React from 'react';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';
import { CreditAgreement, Util } from '@finley-technologies/finley-types';

import {
  isSanitizedJSONWithNumberValues,
  VerificationApproveTablesMode,
} from './verificationTablesUtils';
import { verificationRawValueRoundingErrorAllowance } from 'packages/borrowing-base/ui/borrowingBaseSummary/utils';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { useGetApprovedFundingRequestsMetadataWithBorrowingBaseReportQuery } from 'packages/funding-requests/requests/requests.gql.generated';

type LimitType = 'min' | 'max';

export enum VerificationReportFundingRequestComponentTypes {
  drawRequestTotalAmount = 'drawRequestTotalAmount',
  collectionsRecycleTotalAmount = 'collectionsRecycleTotalAmount',
  collectionsPaydownTotalAmount = 'collectionsPaydownTotalAmount',
  curePaydownTotalAmount = 'curePaydownTotalAmount',
}

export const isVerificationReportFundingRequestComponentTypes = (
  test: any,
): test is VerificationReportFundingRequestComponentTypes => {
  return _.includes(VerificationReportFundingRequestComponentTypes, test);
};

const generateLimitNotesString = ({
  constraintKey,
  limitNotes,
}: {
  constraintKey: string;
  limitNotes: {
    limitValue: number;
    limitValueType: 'number' | 'currency';
    limitType: LimitType;
  };
}) => {
  const limitTypeToKeywordMap: { [key in LimitType]: string } = {
    min: 'not meet',
    max: 'exceed',
  };

  const limitValueFormatted =
    limitNotes.limitValueType === 'currency'
      ? commonUtils.numberToCurrencyWithSymbol({
          number: limitNotes.limitValue,
        })
      : limitNotes.limitValue;

  return `${limitValueFormatted} would ${
    limitTypeToKeywordMap[limitNotes.limitType]
  } ${_.toLower(commonUtils.toSentenceCase(constraintKey))}.`;
};

export const generateIncrementAmountLimitNotes = ({
  fundingRequestComponentAmount,
  fundingRequestComponentType,
  incrementAmount,
}: {
  fundingRequestComponentAmount: number;
  fundingRequestComponentType: VerificationReportFundingRequestComponentTypes;
  incrementAmount: number;
}) => {
  const fundingRequestComponentAmountForIncrement =
    Math.floor(fundingRequestComponentAmount / incrementAmount) *
    incrementAmount;

  return `${commonUtils.toSentenceCase(
    fundingRequestComponentType,
  )} must have an increment amount of ${commonUtils.numberToCurrencyWithSymbol({
    number: incrementAmount,
  })}. For example: ${commonUtils.numberToCurrencyWithSymbol({
    number: fundingRequestComponentAmountForIncrement,
  })}.`;
};

const isMaximumFrequencyApproved = ({
  maximumFrequency,
  currentCount,
}: {
  maximumFrequency: number;
  currentCount: number;
}): boolean => currentCount + 1 <= maximumFrequency;

const isMaximumAmountPerPeriodApproved = ({
  maximumAmountPerPeriod,
  requestAmount,
  currentPeriodAmount,
}: {
  maximumAmountPerPeriod: number;
  requestAmount: number;
  currentPeriodAmount: number;
}): boolean => currentPeriodAmount + requestAmount <= maximumAmountPerPeriod;

export const isIncrementAmountApproved = ({
  fundingRequestComponentAmount,
  incrementAmount,
}: {
  fundingRequestComponentAmount: number;
  incrementAmount: number;
}): boolean => fundingRequestComponentAmount % incrementAmount === 0;

export const isMinimumAmountApproved = ({
  fundingRequestComponentAmount,
  minimumAmount,
}: {
  fundingRequestComponentAmount: number;
  minimumAmount: number;
}): { isApproved: boolean; isRoundingErrorAllowanceUsed: boolean } => {
  const isApproved =
    fundingRequestComponentAmount >=
    minimumAmount - verificationRawValueRoundingErrorAllowance;
  return {
    isApproved,
    isRoundingErrorAllowanceUsed:
      isApproved && fundingRequestComponentAmount < minimumAmount,
  };
};

export const isFundingRequestComponentMaximumConstraintAmountApproved = ({
  fundingRequestComponentAmount,
  fundingRequestComponentMaximumConstraintAmount,
}: {
  fundingRequestComponentAmount: number;
  fundingRequestComponentMaximumConstraintAmount: number;
}): { isApproved: boolean; isRoundingErrorAllowanceUsed: boolean } => {
  const isApproved =
    fundingRequestComponentAmount <=
    fundingRequestComponentMaximumConstraintAmount +
      verificationRawValueRoundingErrorAllowance;
  return {
    isApproved,
    isRoundingErrorAllowanceUsed:
      isApproved &&
      fundingRequestComponentAmount >
        fundingRequestComponentMaximumConstraintAmount,
  };
};

export const getCalendarDateRangeForFrequency = ({
  reportingDate,
  frequency,
}: {
  reportingDate: Util.FullDateStr;
  frequency: Util.FrequencyPeriod;
}): { startDate: number; endDate: number } => {
  const endDate = commonDate.fullDateStrToEpoch(reportingDate);
  return {
    startDate: commonDate.fullDateStrToEpoch(
      commonDate.firstOfPeriod(reportingDate, frequency),
    ),
    endDate,
  };
};

const isReportingDateWithinDateRange = ({
  reportingDate,
  startDateEpoch,
  endDateEpoch,
}: {
  reportingDate: Util.FullDateStr;
  startDateEpoch: number;
  endDateEpoch: number;
}): boolean => {
  return (
    commonDate.fullDateStrToEpoch(reportingDate) <= endDateEpoch &&
    commonDate.fullDateStrToEpoch(reportingDate) >= startDateEpoch
  );
};

const VerificationFundingConstraintValidCell = () => {
  return (
    <Flex
      alignItems="center"
      backgroundColor={colors.green[100]}
      borderRadius="8px"
      gap="16px"
      padding="8px"
      h="fit-content"
      w="124px"
    >
      <Icon icon="circle-check" color={colors.success} size="20px" />
      <Text color={colors.success} fontWeight="bold">
        Valid
      </Text>
    </Flex>
  );
};

const VerificationFundingConstraintInvalidCell = () => {
  return (
    <Flex
      alignItems="center"
      backgroundColor={colors.red[100]}
      borderRadius="8px"
      gap="16px"
      padding="8px"
      h="fit-content"
      w="124px"
    >
      <Icon icon="circle-x" color={colors.error} size="20px" />
      <Text color={colors.error} fontWeight="bold">
        Not Valid
      </Text>
    </Flex>
  );
};

type VerificationFundingConstraintRowProps = {
  constraintKey: string;
  requirement?: string;
  current?: string;
  ifApproved: boolean;
  isRoundingErrorAllowanceUsed?: boolean;
  // the limitNotes obj will be used to construct the notes section if ifApproved===false
  // to explain why it is invalid
  limitNotes: {
    limitValue: number;
    limitValueType: 'number' | 'currency';
    limitType: LimitType;
  };
  limitNotesCustomString?: string;
};
export const VerificationFundingConstraintRow = ({
  constraintKey,
  requirement,
  current,
  ifApproved,
  isRoundingErrorAllowanceUsed,
  limitNotes,
  limitNotesCustomString,
}: VerificationFundingConstraintRowProps) => {
  let limitNotesString: string = '';
  if (!ifApproved) {
    limitNotesString =
      limitNotesCustomString ??
      generateLimitNotesString({ constraintKey, limitNotes });
  }
  if (isRoundingErrorAllowanceUsed) {
    limitNotesString = `The difference in values is ${verificationRawValueRoundingErrorAllowance} or less`;
  }

  return (
    <>
      <GridItem
        borderBottom="1px solid"
        borderColor={colors.gray[100]}
        display="flex"
        alignItems="center"
        p="16px 0px"
        colSpan={2}
      >
        {commonUtils.toTitleCase(constraintKey)}
      </GridItem>
      <GridItem
        borderBottom="1px solid"
        borderColor={colors.gray[100]}
        display="flex"
        alignItems="center"
        p="16px 0px"
        colSpan={2}
      >
        {requirement || '-'}
      </GridItem>
      <GridItem
        borderBottom="1px solid"
        borderColor={colors.gray[100]}
        display="flex"
        alignItems="center"
        p="16px 0px"
        colSpan={2}
      >
        {current || '-'}
      </GridItem>
      <GridItem
        borderBottom="1px solid"
        borderColor={colors.gray[100]}
        display="flex"
        alignItems="center"
        p="16px 0px"
      >
        {ifApproved ? (
          <VerificationFundingConstraintValidCell />
        ) : (
          <VerificationFundingConstraintInvalidCell />
        )}
      </GridItem>
      <GridItem
        borderBottom="1px solid"
        borderColor={colors.gray[100]}
        display="flex"
        alignItems="center"
        p="16px 0px"
        colSpan={2}
      >
        {limitNotesString}
      </GridItem>
    </>
  );
};

type VerificationFundingRequestTableHeaderProps = {
  label?: string;
  colSpan?: number;
};
export const VerificationFundingRequestTableHeader = ({
  label,
  colSpan,
}: VerificationFundingRequestTableHeaderProps) => {
  return (
    <GridItem
      fontSize="16px"
      fontWeight="semibold"
      borderBottom="1px solid"
      borderColor={colors.gray[100]}
      display="flex"
      pb="16px"
      colSpan={colSpan || 1}
    >
      <Text alignSelf="end">{label || ''}</Text>
    </GridItem>
  );
};

type MaximumAmountPerPeriodRowProps = {
  fundingRequestComponentTotalAmount: number;
  fundingRequestComponentTypeKey: VerificationReportFundingRequestComponentTypes;
  maximumAmountPeriod: Util.FrequencyPeriod;
  maximumAmountPerPeriod: number;
  creditFacilityId: string;
  reportingDate: Util.FullDateStr;
};
export const MaximumAmountPerPeriodRow = ({
  fundingRequestComponentTotalAmount,
  fundingRequestComponentTypeKey,
  maximumAmountPerPeriod,
  maximumAmountPeriod,
  creditFacilityId,
  reportingDate,
}: MaximumAmountPerPeriodRowProps) => {
  const toast = useToast();
  const {
    data: approvedFundingRequestsData,
    loading: isApprovedFundingRequestsLoading,
  } = useGetApprovedFundingRequestsMetadataWithBorrowingBaseReportQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: { creditFacilityIds: [creditFacilityId] },
  });

  if (!approvedFundingRequestsData || isApprovedFundingRequestsLoading) {
    return (
      <Skeleton
        height="40px"
        data-testid="verification-draw-request-maximum-amount-per-period-loading"
      />
    );
  }

  const {
    fundingRequests: { nodes },
  } = approvedFundingRequestsData;

  const { startDate, endDate } = getCalendarDateRangeForFrequency({
    reportingDate,
    frequency: maximumAmountPeriod,
  });

  const approvedFundingRequestsWithinDateRange = _.filter(
    nodes,
    (node) =>
      node[fundingRequestComponentTypeKey] &&
      isReportingDateWithinDateRange({
        reportingDate: node.reportingDate,
        startDateEpoch: startDate,
        endDateEpoch: endDate,
      }),
  );

  const totalFundingRequestComponentAmountForPeriod = _.sumBy(
    approvedFundingRequestsWithinDateRange,
    fundingRequestComponentTypeKey,
  );

  return (
    <VerificationFundingConstraintRow
      constraintKey="maximumAmountPerPeriod"
      requirement={`${commonUtils.numberToCurrencyWithSymbol({
        number: maximumAmountPerPeriod,
      })} per ${maximumAmountPeriod}`}
      current={commonUtils.numberToCurrencyWithSymbol({
        number: totalFundingRequestComponentAmountForPeriod,
      })}
      ifApproved={isMaximumAmountPerPeriodApproved({
        maximumAmountPerPeriod,
        requestAmount: fundingRequestComponentTotalAmount,
        currentPeriodAmount: totalFundingRequestComponentAmountForPeriod,
      })}
      limitNotes={{
        limitValue:
          totalFundingRequestComponentAmountForPeriod +
          fundingRequestComponentTotalAmount,
        limitValueType: 'currency',
        limitType: 'max',
      }}
    />
  );
};

type MaximumFrequencyRowProps = {
  creditFacilityId: string;
  maximumFrequency: CreditAgreement.DomainEntity.MaximumFrequencyProperty;
  reportingDate: Util.FullDateStr;
  fundingRequestComponentTypeKey: VerificationReportFundingRequestComponentTypes;
};
export const MaximumFrequencyRow = ({
  creditFacilityId,
  maximumFrequency,
  reportingDate,
  fundingRequestComponentTypeKey,
}: MaximumFrequencyRowProps) => {
  const toast = useToast();
  const {
    data: approvedFundingRequestsData,
    loading: isApprovedFundingRequestsLoading,
  } = useGetApprovedFundingRequestsMetadataWithBorrowingBaseReportQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: { creditFacilityIds: [creditFacilityId] },
  });

  if (!approvedFundingRequestsData || isApprovedFundingRequestsLoading) {
    return (
      <Skeleton
        height="40px"
        data-testid="verification-funding-request-component-maximum-amount-per-period-loading"
      />
    );
  }

  const {
    fundingRequests: { nodes },
  } = approvedFundingRequestsData;

  const { value, frequencyType, frequencyPeriod } = maximumFrequency;

  const { startDate, endDate } = getCalendarDateRangeForFrequency({
    reportingDate,
    frequency: frequencyPeriod,
  });

  const totalFundingRequestComponentCountForPeriod = _.filter(
    nodes,
    (node) =>
      node[fundingRequestComponentTypeKey] &&
      node[fundingRequestComponentTypeKey] > 0 &&
      isReportingDateWithinDateRange({
        reportingDate: node.reportingDate,
        startDateEpoch: startDate,
        endDateEpoch: endDate,
      }),
  ).length;

  return (
    <VerificationFundingConstraintRow
      constraintKey="maximumFrequency"
      requirement={`${value} per ${frequencyType} ${frequencyPeriod}`}
      current={_.toString(totalFundingRequestComponentCountForPeriod)}
      ifApproved={isMaximumFrequencyApproved({
        maximumFrequency: value,
        currentCount: totalFundingRequestComponentCountForPeriod,
      })}
      limitNotes={{
        limitValue: totalFundingRequestComponentCountForPeriod + 1,
        limitValueType: 'number',
        limitType: 'max',
      }}
    />
  );
};

type CurrencyRowWithDividerProps = {
  label: string;
  value: number;
};
export const CurrencyRowWithDivider = ({
  label,
  value,
}: CurrencyRowWithDividerProps) => {
  return (
    <Box mb="16px">
      <Flex mb="8px">
        <Text fontSize="18px" fontWeight={600}>
          {commonUtils.toTitleCase(label)}
        </Text>
        <Spacer />
        <Text fontSize="18px" fontWeight={600}>
          {commonUtils.numberToCurrencyWithSymbol({ number: value })}
        </Text>
      </Flex>
      <Divider opacity="100%" />
    </Box>
  );
};

type VerificationFundingRequestComponentTableProps = {
  fundingRequestComponentRows: React.ReactElement;
  fundingRequestComponentType: string;
  fundingRequestComponentTotalAmount: Util.SanitizedJSON;
  viewMode: VerificationApproveTablesMode;
};
export const VerificationFundingRequestComponentTable = ({
  fundingRequestComponentRows,
  fundingRequestComponentType,
  fundingRequestComponentTotalAmount,
  viewMode,
}: VerificationFundingRequestComponentTableProps) => {
  return (
    <Box p="0 16px" mb="48px">
      {isSanitizedJSONWithNumberValues(fundingRequestComponentTotalAmount) && (
        <Box>
          <CurrencyRowWithDivider
            key={`funding-component-total-${fundingRequestComponentType}`}
            label={fundingRequestComponentType}
            value={fundingRequestComponentTotalAmount.borrowerValue ?? 0}
          />

          {viewMode === 'approveDeny' && (
            <Grid
              templateColumns="repeat(9, 1fr)"
              templateRows="repeat(1, 56px)"
            >
              <VerificationFundingRequestTableHeader colSpan={2} />
              <VerificationFundingRequestTableHeader
                label="Requirement"
                colSpan={2}
              />
              <VerificationFundingRequestTableHeader
                label="Current"
                colSpan={2}
              />
              <VerificationFundingRequestTableHeader
                label="Status"
                colSpan={1}
              />
              <VerificationFundingRequestTableHeader
                label="Warning"
                colSpan={2}
              />
              {fundingRequestComponentRows}
            </Grid>
          )}
        </Box>
      )}
    </Box>
  );
};
